/**
 * Display helpers for the asset Conformité tab: the words a rule's
 * applicability condition and severity are shown with. Machine values (enum
 * codes, severity ranks) never reach the screen as such.
 */

/** Value → localized label, per property code (enum properties only). */
export type EnumLabelsByCode = Record<string, Record<string, string>>

type LocalizedName = { [lang: string]: string | undefined } | string | null | undefined

interface EnumValueLike {
    value?: string
    name?: LocalizedName
}

/** Reads a localized name bag in lang, then English, then any language. */
export function pickName(name: LocalizedName, lang: string): string {
    if (!name) return ''
    if (typeof name === 'string') return name
    return name[lang] || name.en || Object.values(name).find((v) => !!v) || ''
}

/** Builds the value → label table of one enum property, in lang. */
export function enumLabels(values: EnumValueLike[] | null | undefined, lang: string): Record<string, string> {
    const out: Record<string, string> = {}
    for (const v of values || []) {
        if (!v?.value) continue
        const label = pickName(v.name, lang)
        if (label) out[v.value] = label
    }
    return out
}

/**
 * An enum code shown with its label ("technical" → "Technique"); a value with
 * no label (a number, a code the ontology lost) is shown as is. Arrays (the
 * `in` operator) map element-wise.
 */
export function labelConditionValue(value: unknown, labels: Record<string, string> | undefined): unknown {
    if (!labels) return value
    if (Array.isArray(value)) return value.map((v) => labelConditionValue(v, labels))
    if (typeof value === 'string' && labels[value]) return labels[value]
    return value
}

/** Every property code a rule's conditions expression reads. */
export function conditionPropertyCodes(expr: unknown, out: Set<string> = new Set()): Set<string> {
    if (!expr || typeof expr !== 'object') return out
    const e = expr as Record<string, unknown>
    if (Array.isArray(e.all)) e.all.forEach((x) => conditionPropertyCodes(x, out))
    if (Array.isArray(e.any)) e.any.forEach((x) => conditionPropertyCodes(x, out))
    if (typeof e.property === 'string') out.add(e.property)
    return out
}

const SEVERITY_LABEL_KEYS: Record<number, string> = {
    1: 'rules.severity_critical',
    2: 'rules.severity_high',
    3: 'rules.severity_medium',
    4: 'rules.severity_low',
    5: 'rules.severity_lowest',
}

/** The i18n key (compliance namespace) of a severity rank; medium when unset. */
export function severityLabelKey(severity: number | null | undefined): string {
    return SEVERITY_LABEL_KEYS[severity || 3] || SEVERITY_LABEL_KEYS[3]
}
